import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { LuBuilding2, LuMail, LuLock, LuEye, LuEyeOff, LuArrowLeft } from 'react-icons/lu';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { login, user, loading: authLoading, requestPasswordReset } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('login.welcomeBack'));
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || t('login.authFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    try {
      await requestPasswordReset(email);
      setResetSent(true);
      toast.success(t('login.resetLinkSent'));
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (authLoading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-500 font-medium font-sans">{t('login.authenticating')}</p>
        </div>
      </div>);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 space-y-8 animate-in fade-in zoom-in duration-300">
          
          {!isForgotPassword ?
          <>
              <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-3xl shadow-lg shadow-blue-200 mb-2 transform rotate-3 hover:rotate-0 transition-transform">
                  <LuBuilding2 className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('login.title')}</h1>
                <p className="text-gray-500 font-medium">{t('login.subtitle')}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-1">{t('login.emailLabel')}</label>
                  <div className="relative group">
                    <LuMail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 focus:bg-white outline-none transition-all"
                    placeholder={t('login.emailPlaceholder')}
                    required
                    disabled={loading} />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <label className="text-sm font-bold text-gray-700">{t('login.passwordLabel')}</label>
                    <button
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                    className="text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline">

                      {t('login.forgotPassword')}
                    </button>
                  </div>
                  <div className="relative group">
                    <LuLock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 focus:bg-white outline-none transition-all"
                    placeholder={t('login.passwordPlaceholder')}
                    required
                    disabled={loading} />

                    <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors p-1">
                      {showPassword ? <LuEyeOff className="w-5 h-5" /> : <LuEye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl transition-all shadow-lg shadow-blue-100 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed mt-2">
                  {loading ?
                <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t('login.verifying')}
                    </span> :
                t('login.signInButton')
                }
                </button>
              </form>
            </> :

          <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div className="space-y-2">
                <button
                onClick={() => {
                  setIsForgotPassword(false);
                  setResetSent(false);
                }}
                className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-blue-600 transition-colors mb-4 group">

                  <LuArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  {t('login.backToLogin')}
                </button>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('login.resetPassword')}</h1>
                <p className="text-gray-500 font-medium">{t('login.resetInstructions')}</p>
              </div>

              {!resetSent ?
            <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-1">{t('login.emailLabel')}</label>
                    <div className="relative group">
                      <LuMail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                      <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 focus:bg-white outline-none transition-all"
                    placeholder={t('login.emailPlaceholder')}
                    required
                    disabled={loading} />
                    </div>
                  </div>

                  <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl transition-all shadow-lg shadow-blue-100 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed">
                    {loading ?
                <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        {t('login.sendingInstructions')}
                      </span> :
                t('login.sendResetLink')
                }
                  </button>
                </form> :

            <div className="bg-green-50 border border-green-100 rounded-2xl p-6 text-center space-y-3 animate-in fade-in zoom-in duration-500">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg shadow-green-100">
                    <LuMail className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-green-900">{t('login.resetLinkSent')}</h3>
                  <p className="text-sm text-green-700 leading-relaxed">{t('login.checkEmail')}</p>
                  <button
                onClick={() => setIsForgotPassword(false)}
                className="mt-4 text-sm font-bold text-green-700 hover:text-green-800 underline">

                    {t('login.backToLogin')}
                  </button>
                </div>
            }
            </div>
          }

          <div className="pt-4 flex flex-col items-center gap-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full border border-gray-100">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-xs font-bold text-gray-500 tracking-wide uppercase">{t('login.systemOperational')}</span>
            </div>
            <p className="text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em]">{t('login.version')}</p>
          </div>
        </div>
      </div>
    </div>);
}